import { LastDailyBar } from "../models/charts";

// Shared "last bar seen per series" cache, keyed by `${full_name}|${resolution}`.
// Written by the datafeed on every firstDataRequest getBars; read by the
// streaming provider to reseed a bar handler after a post-reconnect /history
// refetch (see the pendingReload path), so ticks resume on the freshly
// backfilled history instead of the stale pre-gap bar. Kept in its own module
// so both the datafeed and the streaming provider can share it without a
// circular import between them.
//
// The RESOLUTION is part of the key because a multiple-chart layout can show
// the same symbol at two resolutions (5m and 1D side by side), which are two
// independent series. Keyed by symbol alone they shared one entry: whichever
// getBars landed last won, and the other handler's post-reconnect reseed picked
// up a bar from the WRONG resolution — a 5m stream resuming on a day-bucketed
// bar with day-cumulative volume, and vice versa. Always go through
// lastBarKey().
export const LastBarsCache = new Map<string, LastDailyBar>();

// Canonical LastBarsCache key. Resolution is stringified because TV hands it to
// the datafeed as a string ('5', '1D') but chart APIs can report a
// ResolutionString object — both must land on the same key.
export function lastBarKey(fullName: string | undefined, resolution: string | undefined): string {
    return `${fullName ?? ''}|${resolution ?? ''}`;
}

// Newest cached last bar for a symbol at ANY resolution. For consumers that
// only want a recent price (tab title, header legend) and genuinely don't care
// which series it came from. Bar-accounting paths must never guess — they use
// LastBarsCache.get(lastBarKey(...)) with their own resolution.
export function anyLastBarFor(fullName: string | undefined): LastDailyBar | undefined {
    const prefix = `${fullName ?? ''}|`;
    for (const [key, bar] of LastBarsCache) {
        if (key.startsWith(prefix)) return bar;
    }
    return undefined;
}

// Newest HISTORY bar time per `${full_name}|${resolution}`, written by the
// datafeed on every non-empty getBars result (monotonic — only raised, so an
// older-history backfill page can't lower it). The stream clamp uses it as a
// floor: v32 re-fetches history on every visibility resume, and a late tick
// racing that reseed used to get pushed BEHIND history's last bar — the
// "time order violation" errors (tv-upgrade/09; repro: sparse MCX symbol on
// 5S at the live edge). The per-handler _lastPushedBarTime clamp can't see
// those because history bypasses the push path.
export const HistoryTailCache = new Map<string, number>();
